import React, { useState } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { leaguesApi } from '../../api/leagues';
import { Match } from '../../api/matches';
import LeagueStatusBadge from '../../components/LeagueStatusBadge';
import { useTranslation } from 'react-i18next';
import { getDateLocale } from '../../i18n';
import { TrashButton } from '../../components/IconButton';

export default function AdminLeagueManage() {
  const { id } = useParams<{ id: string }>();
  const leagueId = parseInt(id!);
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const [actionError, setActionError] = useState('');
  const [actionSuccess, setActionSuccess] = useState('');
  const [waiverModal, setWaiverModal] = useState<{ regId: number; name: string } | null>(null);
  const [waiverNote, setWaiverNote] = useState('');
  const { t } = useTranslation();

  const STATUS_OPTIONS = [
    { value: 'draft', label: t('status.draft') },
    { value: 'open', label: t('status.open') },
    { value: 'closed', label: t('status.closed') },
  ];

  const { data: league } = useQuery({
    queryKey: ['league', leagueId],
    queryFn: () => leaguesApi.get(leagueId).then((r) => r.data),
  });

  const { data: registrations = [] } = useQuery({
    queryKey: ['league-registrations', leagueId],
    queryFn: () => leaguesApi.getRegistrations(leagueId).then((r) => r.data),
  });

  const setPaymentMutation = useMutation({
    mutationFn: ({ regId, status, note }: { regId: number; status: string; note?: string }) =>
      leaguesApi.setPaymentStatus(leagueId, regId, status, note),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['league-registrations', leagueId] });
      queryClient.invalidateQueries({ queryKey: ['league', leagueId] });
      setActionSuccess(t('admin.msgPaymentUpdated'));
      setWaiverModal(null);
      setWaiverNote('');
      setTimeout(() => setActionSuccess(''), 3000);
    },
    onError: (err: unknown) => {
      const e = err as { response?: { data?: { detail?: string } } };
      setActionError(e.response?.data?.detail ?? t('admin.error'));
    },
  });

  const removeRegMutation = useMutation({
    mutationFn: (regId: number) => leaguesApi.removeRegistration(leagueId, regId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['league-registrations', leagueId] });
      queryClient.invalidateQueries({ queryKey: ['league', leagueId] });
      setActionSuccess(t('admin.msgRegRemoved'));
      setTimeout(() => setActionSuccess(''), 3000);
    },
    onError: (err: unknown) => {
      const e = err as { response?: { data?: { detail?: string } } };
      setActionError(e.response?.data?.detail ?? t('admin.errorRemove'));
    },
  });

  const deleteMutation = useMutation({
    mutationFn: () => leaguesApi.delete(leagueId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['leagues'] });
      queryClient.invalidateQueries({ queryKey: ['admin-leagues'] });
      navigate('/admin');
    },
    onError: (err: unknown) => {
      const e = err as { response?: { data?: { detail?: string } } };
      setActionError(e.response?.data?.detail ?? t('admin.errorDelete'));
    },
  });

  const updateStatusMutation = useMutation({
    mutationFn: (status: string) => leaguesApi.update(leagueId, { status }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['league', leagueId] });
      queryClient.invalidateQueries({ queryKey: ['admin-leagues'] });
      queryClient.invalidateQueries({ queryKey: ['leagues'] });
      setActionSuccess(t('admin.msgStatusUpdated'));
      setTimeout(() => setActionSuccess(''), 3000);
    },
    onError: (err: unknown) => {
      const e = err as { response?: { data?: { detail?: string } } };
      setActionError(e.response?.data?.detail ?? t('admin.error'));
    },
  });

  const finishMutation = useMutation({
    mutationFn: () => leaguesApi.finish(leagueId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['league', leagueId] });
      queryClient.invalidateQueries({ queryKey: ['admin-leagues'] });
      queryClient.invalidateQueries({ queryKey: ['leagues'] });
      setActionSuccess(t('admin.msgLeagueFinished'));
      setTimeout(() => setActionSuccess(''), 3000);
    },
    onError: (err: unknown) => {
      const e = err as { response?: { data?: { detail?: string } } };
      setActionError(e.response?.data?.detail ?? t('admin.error'));
    },
  });

  const startKnockoutMutation = useMutation({
    mutationFn: () => leaguesApi.startKnockout(leagueId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['league', leagueId] });
      queryClient.invalidateQueries({ queryKey: ['league-group-matches', leagueId] });
      queryClient.invalidateQueries({ queryKey: ['admin-leagues'] });
      queryClient.invalidateQueries({ queryKey: ['leagues'] });
      setActionSuccess(t('admin.msgKOStarted'));
      setActionError('');
      setTimeout(() => setActionSuccess(''), 4000);
    },
    onError: (err: unknown) => {
      const e = err as { response?: { data?: { detail?: string } } };
      setActionError(e.response?.data?.detail ?? t('admin.error'));
    },
  });

  const isGroupsKo = league?.league_format !== 'round_robin';

  const { data: groupMatches = [] } = useQuery({
    queryKey: ['league-group-matches', leagueId],
    queryFn: () => leaguesApi.getMatches(leagueId, { phase: 'group' }).then((r) => r.data as Match[]),
    enabled: !!league && league.status === 'active' && isGroupsKo,
  });

  if (!league) return <div className="text-center py-16">{t('admin.loading')}</div>;

  const confirmedCount = (registrations as any[]).filter((r) => r.payment_status === 'confirmed').length;
  const pendingCount = (registrations as any[]).filter((r) => r.payment_status === 'pending').length;
  const waivedCount = (registrations as any[]).filter((r) => r.payment_status === 'waived').length;

  const totalGroupMatches = (groupMatches as Match[]).filter((m) => !m.is_bye).length;
  const doneGroupMatches = (groupMatches as Match[]).filter(
    (m) => !m.is_bye && (m.status === 'confirmed' || m.status === 'cancelled')
  ).length;
  const allGroupMatchesDone = totalGroupMatches > 0 && doneGroupMatches === totalGroupMatches;
  const showKoButton = league.status === 'active' && league.league_format !== 'round_robin';

  return (
    <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
      <div className="flex items-center gap-3 mb-6">
        <Link to="/admin" className="text-primary-600 hover:text-primary-700 text-sm">{t('admin.backToAdmin')}</Link>
        <span className="text-gray-300">/</span>
        <h1 className="text-xl font-bold text-gray-900">{league.name}</h1>
        <LeagueStatusBadge status={league.status} />
      </div>

      {/* Waiver-Kommentar Modal */}
      {waiverModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 w-full max-w-md shadow-xl">
            <h3 className="text-lg font-semibold text-gray-900 mb-1">{t('admin.waiverModalTitle')}</h3>
            <p className="text-sm text-gray-500 mb-4">
              {t('admin.waiverModalFor')} <strong>{waiverModal.name}</strong>
            </p>
            <label className="label">{t('admin.waiverModalLabel')} <span className="text-red-500">*</span></label>
            <textarea
              value={waiverNote}
              onChange={(e) => setWaiverNote(e.target.value)}
              className="input mb-4"
              rows={3}
              placeholder={t('admin.waiverModalPlaceholder')}
              autoFocus
            />
            <div className="flex justify-end gap-3">
              <button onClick={() => { setWaiverModal(null); setWaiverNote(''); }} className="btn btn-secondary">
                {t('admin.cancel')}
              </button>
              <button
                onClick={() => setPaymentMutation.mutate({ regId: waiverModal.regId, status: 'waived', note: waiverNote })}
                disabled={!waiverNote.trim() || setPaymentMutation.isPending}
                className="btn btn-primary"
              >
                {t('admin.waiverModalSubmit')}
              </button>
            </div>
          </div>
        </div>
      )}

      {actionError && (
        <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-md text-sm mb-4">
          {actionError}
        </div>
      )}
      {actionSuccess && (
        <div className="bg-green-50 border border-green-200 text-green-700 px-4 py-3 rounded-md text-sm mb-4">
          {actionSuccess}
        </div>
      )}

      {/* Actions */}
      <div className="card mb-6">
        <h2 className="font-semibold text-gray-900 mb-4">{t('admin.actions')}</h2>
        <div className="flex flex-wrap gap-3">
          {['draft', 'open', 'closed'].includes(league.status) && (
            <Link to={`/admin/leagues/${leagueId}/draw`} className="btn btn-primary btn-sm">
              {t('admin.startDraw')}
            </Link>
          )}
          {['active', 'knockout', 'finished'].includes(league.status) && (
            <Link to={`/admin/leagues/${leagueId}/draw`} className="btn btn-secondary btn-sm">
              {t('admin.groupsTable')}
            </Link>
          )}
          {['active', 'knockout'].includes(league.status) && (
            <button
              onClick={() => {
                if (window.confirm(t('admin.finishConfirm', { name: league.name }))) {
                  setActionError('');
                  finishMutation.mutate();
                }
              }}
              disabled={finishMutation.isPending}
              className="btn btn-secondary btn-sm border-amber-300 text-amber-700 hover:bg-amber-50"
            >
              {finishMutation.isPending ? t('admin.finishing') : t('admin.finishLeague')}
            </button>
          )}
          {showKoButton && (
            allGroupMatchesDone ? (
              <button
                onClick={() => {
                  if (window.confirm(t('admin.koConfirm'))) {
                    setActionError('');
                    startKnockoutMutation.mutate();
                  }
                }}
                disabled={startKnockoutMutation.isPending}
                className="btn btn-sm bg-green-600 hover:bg-green-700 text-white border-green-600 flex items-center gap-1.5"
              >
                <span className="inline-block w-2 h-2 rounded-full bg-green-300" />
                {startKnockoutMutation.isPending ? t('admin.starting') : t('admin.startKO')}
              </button>
            ) : (
              <span className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-md text-sm bg-gray-100 text-gray-500 border border-gray-200">
                <span className="inline-block w-2 h-2 rounded-full bg-yellow-400" />
                {t('admin.groupMatchesProgress', { done: doneGroupMatches, total: totalGroupMatches })}
              </span>
            )
          )}
          {['open', 'closed', 'draft'].includes(league.status) && (
            STATUS_OPTIONS.map((opt) =>
              opt.value !== league.status && (
                <button
                  key={opt.value}
                  onClick={() => updateStatusMutation.mutate(opt.value)}
                  disabled={updateStatusMutation.isPending}
                  className="btn btn-secondary btn-sm"
                >
                  {t('admin.statusLabel', { label: opt.label })}
                </button>
              )
            )
          )}
          <TrashButton
            onClick={() => {
              if (window.confirm(t('admin.deleteConfirm', { name: league.name }))) {
                setActionError('');
                deleteMutation.mutate();
              }
            }}
            disabled={deleteMutation.isPending}
            title={t('admin.deleteLeague')}
            className="ml-auto"
          />
        </div>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 mb-6">
        <div className="card text-center">
          <div className="text-3xl font-bold text-gray-900">{(registrations as any[]).length}</div>
          <div className="text-sm text-gray-500 mt-1">{t('admin.registrationCount')}</div>
        </div>
        <div className="card text-center">
          <div className="text-3xl font-bold text-green-600">{confirmedCount}</div>
          <div className="text-sm text-gray-500 mt-1">{t('admin.confirmedCount')}</div>
        </div>
        <div className="card text-center">
          <div className="text-3xl font-bold text-yellow-600">{pendingCount}</div>
          <div className="text-sm text-gray-500 mt-1">{t('admin.pendingCount')}</div>
        </div>
        <div className="card text-center">
          <div className="text-3xl font-bold text-blue-500">{waivedCount}</div>
          <div className="text-sm text-gray-500 mt-1">{t('admin.waivedCount')}</div>
        </div>
      </div>

      {/* Registrations table */}
      <div className="card">
        <h2 className="font-semibold text-gray-900 mb-4">{t('admin.registrationCount')}</h2>
        {(registrations as any[]).length === 0 ? (
          <p className="text-gray-500 text-center py-8">{t('admin.noRegistrations')}</p>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-gray-200">
                  <th className="text-left py-2 pr-4 text-gray-500 font-medium">{t('admin.colNamePairTeam')}</th>
                  <th className="text-left py-2 pr-4 text-gray-500 font-medium">{t('admin.colRegistered')}</th>
                  <th className="text-left py-2 pr-4 text-gray-500 font-medium">{t('admin.colPayment')}</th>
                  <th className="py-2 pr-4 text-gray-500 font-medium">{t('admin.colPaymentStatus')}</th>
                  <th className="py-2 text-gray-500 font-medium">{t('admin.colActions')}</th>
                </tr>
              </thead>
              <tbody>
                {(registrations as any[]).map((reg) => {
                  let displayName = '–';
                  if (reg.pair) {
                    const p1 = reg.pair.player1;
                    const p2 = reg.pair.player2;
                    displayName = [
                      p1 ? `${p1.first_name} ${p1.last_name}` : '?',
                      p2 ? `${p2.first_name} ${p2.last_name}` : `(${t('admin.pendingCount')})`,
                    ].join(' / ');
                  } else if (reg.team) {
                    displayName = reg.team.name;
                  } else if (reg.user) {
                    displayName = `${reg.user.first_name} ${reg.user.last_name}`;
                  }

                  return (
                    <tr key={reg.id} className="border-b border-gray-100 last:border-0">
                      <td className="py-2 pr-4 font-medium text-gray-900">{displayName}</td>
                      <td className="py-2 pr-4 text-gray-500">
                        {new Date(reg.registered_at).toLocaleDateString(getDateLocale())}
                      </td>
                      <td className="py-2 pr-4">
                        <span className={`badge ${
                          reg.payment_status === 'confirmed' ? 'bg-green-100 text-green-700' :
                          reg.payment_status === 'pending' ? 'bg-yellow-100 text-yellow-700' :
                          reg.payment_status === 'waived' ? 'bg-blue-100 text-blue-700' :
                          'bg-gray-100 text-gray-500'
                        }`}>
                          {reg.payment_status === 'confirmed' ? t('admin.paymentConfirmed') :
                           reg.payment_status === 'pending' ? t('admin.paymentPending') :
                           reg.payment_status === 'waived' ? t('admin.paymentWaived') : reg.payment_status}
                        </span>
                      </td>
                      <td className="py-2 pr-4">
                        {['draft', 'open', 'closed'].includes(league.status) && (
                          <select
                            value={reg.payment_status}
                            onChange={(e) => {
                              setActionError('');
                              if (e.target.value === 'waived') {
                                setWaiverNote('');
                                setWaiverModal({ regId: reg.id, name: displayName });
                              } else {
                                setPaymentMutation.mutate({ regId: reg.id, status: e.target.value });
                              }
                            }}
                            disabled={setPaymentMutation.isPending}
                            className="text-xs border border-gray-200 rounded px-2 py-1 bg-white text-gray-700 focus:outline-none focus:ring-1 focus:ring-primary-400"
                          >
                            <option value="pending">{t('admin.paymentPending')}</option>
                            <option value="confirmed">{t('admin.paymentConfirmed')}</option>
                            <option value="waived">{t('admin.paymentWaived')}</option>
                          </select>
                        )}
                      </td>
                      <td className="py-2 text-right">
                        <div className="flex items-center justify-end gap-1">
                          {reg.waiver_note && (
                            <div className="relative group">
                              <span className="inline-flex items-center justify-center w-5 h-5 rounded-full bg-blue-100 text-blue-600 text-xs font-bold cursor-default select-none">i</span>
                              <div className="absolute right-0 bottom-full mb-2 w-64 bg-gray-900 text-white text-xs rounded-lg px-3 py-2 opacity-0 group-hover:opacity-100 transition-opacity pointer-events-none z-10 shadow-lg">
                                <p className="font-semibold mb-1">{t('admin.waiverTooltipTitle')}</p>
                                <p>{reg.waiver_note}</p>
                                {reg.waiver && (
                                  <p className="mt-1 text-gray-400">{t('admin.waiverTooltipBy', { name: `${reg.waiver.first_name} ${reg.waiver.last_name}` })}</p>
                                )}
                              </div>
                            </div>
                          )}
                        {['draft', 'open', 'closed'].includes(league.status) && (
                          <TrashButton
                            onClick={() => {
                              if (window.confirm(t('admin.removeConfirm', { name: displayName }))) {
                                setActionError('');
                                removeRegMutation.mutate(reg.id);
                              }
                            }}
                            disabled={removeRegMutation.isPending}
                            title={t('admin.removeReg')}
                          />
                        )}
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}
